// Pieces Meetings and Record share: the clock, the minutes states, the stage waveform, the player with topic marks,
// the minutes, the transcript with its search, and "Ask about it".
import { useEffect, useMemo, useRef, useState, type FormEvent, type MutableRefObject } from 'react';
import { useReducedMotion, type MotionValue } from 'motion/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { MeetingMinutes, MinutesState, Recording, Speaker, TranscriptLine } from '@dpw/shared';
import { api } from '../api';
import { ErrorNote } from '../ui/bits';
import { useAssistantPanel } from '../assistant/Panel';

export const clock = (s: number) => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(Math.floor(s % 60)).padStart(2, '0')}`;

/** SMeetings: the state of a meeting's minutes, its colour. */
export const MINUTES_STATE: Record<MinutesState, [string, string]> = {
  wait: ['Waiting for review', 'var(--amb)'], ok: ['Approved and filed', 'var(--grn)'], none: ['Not recorded', 'var(--line2)'],
  up: ['Upcoming', 'var(--ink)'], processing: ['Writing the minutes', 'var(--ai)'],
};

/** A speaker's dot colour, the same for the same name everywhere. */
const SPEAKER = ['#9B7BFF', '#3CC9B8', '#F5A524', '#38BDF8', '#F472B6', '#A3E635'];
export function speakerColor(name: string) {
  let h = 0;
  for (const ch of name) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return SPEAKER[h % SPEAKER.length]!;
}

/** The stage waveform: 64 bars following the microphone, each a scaleY transform; one steady level with reduced motion. */
export function StageWave({ bars, level, on }: { bars: MutableRefObject<number[]>; level: MotionValue<number>; on: boolean }) {
  const box = useRef<HTMLDivElement>(null);
  const reduce = useReducedMotion();
  useEffect(() => {
    let raf = 0;
    const draw = () => {
      const els = box.current?.children;
      if (els) {
        const src = !on ? [] : reduce ? Array(els.length).fill(level.get()) : bars.current.slice(-els.length);
        for (let i = 0; i < els.length; i++) (els[i] as HTMLElement).style.transform = `scaleY(${0.08 + (src[i] ?? 0) * 0.92})`;
      }
      raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(raf);
  }, [bars, level, reduce, on]);
  return <div className="wave" ref={box} aria-hidden="true">{Array.from({ length: 64 }, (_, i) => <i key={i} />)}</div>;
}

/** The whole recording, with the topics marked on the bar; a topic's time plays from there. */
export function Player({ src, secs, marks, seekTo }: { src: string | null; secs: number; marks: number[]; seekTo: { at: number; n: number } | null }) {
  const audio = useRef<HTMLAudioElement>(null);
  const [pos, setPos] = useState(0);
  const [playing, setPlaying] = useState(false);
  /** The browser would not play without a tap (iOS): say so instead of failing silently. */
  const [refused, setRefused] = useState(false);
  const play = (a: HTMLAudioElement) => a.play().then(() => setRefused(false), (e: unknown) => { if (e instanceof DOMException && e.name === 'NotAllowedError') setRefused(true); });
  useEffect(() => {
    if (!seekTo || !audio.current) { if (seekTo) setPos(seekTo.at); return; }
    audio.current.currentTime = seekTo.at;
    setPos(seekTo.at);
    // A topic's time was tapped: this runs in that tap's task, so it may play; if not, the play button says so.
    void play(audio.current);
  }, [seekTo]);
  const pct = secs ? Math.min(1, pos / secs) : 0;
  return (
    <div className="player" role="group" aria-label="Recording">
      {src && <audio ref={audio} src={src} preload="none" onTimeUpdate={e => setPos(e.currentTarget.currentTime)} onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)} />}
      <button type="button" className="play" disabled={!src} aria-label={playing ? 'Pause' : refused ? `Tap to play from ${clock(pos)}` : 'Play'} onClick={() => { const a = audio.current; if (!a) return; if (a.paused) void play(a); else a.pause(); }}>
        {playing ? <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><rect x="6" y="5" width="4" height="14" rx="1" /><rect x="14" y="5" width="4" height="14" rx="1" /></svg>
          : <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M8 5.5v13l11-6.5z" /></svg>}
      </button>
      <span className="xs mono">{clock(pos)}</span>
      <div className="track2" aria-hidden="true"><i style={{ transform: `scaleX(${pct})` }} />{marks.map(m => <u key={m} style={{ left: `${secs ? (m / secs) * 100 : 0}%` }} />)}</div>
      <span className="xs mono mut">{clock(secs)}</span>
      {refused && <span className="xs mut" role="status">Tap play to listen from {clock(pos)}</span>}
    </div>
  );
}

/** Summary, What was discussed (each time plays from there), Decisions, Next steps. */
export function MinutesBody({ m, onSeek }: { m: MeetingMinutes; onSeek?: (at: number) => void }) {
  return (
    <div className="col" style={{ gap: 16 }}>
      <div className="mini ai"><span className="k" style={{ color: 'var(--ai-tx)' }}>Summary by the assistant</span><div className="sm" style={{ lineHeight: 1.55 }}>{m.summary}</div></div>
      {m.topics.length > 0 && <div className="sec2"><span className="k">What was discussed</span>
        {m.topics.map(t => (
          <div className="topic" key={`${t.at}-${t.heading}`}>
            {onSeek ? <button type="button" className="ts" onClick={() => onSeek(t.at)} aria-label={`Play from ${clock(t.at)}`}>{clock(t.at)}</button> : <span className="ts">{clock(t.at)}</span>}
            <div><b>{t.heading}</b>{t.detail ? <div className="mut">{t.detail}</div> : null}</div>
          </div>
        ))}
      </div>}
      {m.decisions.length > 0 && <div className="sec2"><span className="k">Decisions</span>{m.decisions.map(d => <div className="topic" key={d}><span>{d}</span></div>)}</div>}
      {m.next.length > 0 && <div className="sec2"><span className="k">Next steps discussed</span>{m.next.map(d => <div className="topic" key={d}><span>{d}</span></div>)}</div>}
    </div>
  );
}

/** How a voice shows: its confirmed name; the assistant's guess (violet, until someone confirms it); or its label. */
export function speakerView(who: string, speakers: Speaker[]): { name: string; state: 'named' | 'suggested' | 'unknown' } {
  const s = speakers.find(x => x.label === who);
  if (!s) return { name: who, state: 'named' };
  if (s.confirmed && s.name) return { name: s.name, state: 'named' };
  if (s.suggested) return { name: s.suggested, state: 'suggested' };
  return { name: s.label, state: 'unknown' };
}

/** Name a voice: pick from the people who could be in the meeting, or type a name. Applies to the whole transcript. */
export function NameSpeaker({ rec, label, onDone }: { rec: Recording; label: string; onDone: () => void }) {
  const qc = useQueryClient();
  const cur = rec.speakers.find(s => s.label === label);
  const [name, setName] = useState(cur?.name ?? cur?.suggested ?? '');
  const save = useMutation({ mutationFn: (n: string) => api.recordings.nameSpeaker(rec.id, label, n), onSuccess: () => { void qc.invalidateQueries(); onDone(); } });
  return (
    <form className="spkform" aria-label={`Who is ${label}?`} onSubmit={(e: FormEvent) => { e.preventDefault(); if (name.trim()) save.mutate(name.trim()); }}>
      <span className="xs mut">Who is {label}? It changes the whole transcript.</span>
      <input className="inp" list={`people-${rec.id}`} value={name} onChange={e => setName(e.target.value)} aria-label="Name" autoFocus />
      <datalist id={`people-${rec.id}`}>{rec.people.map(p => <option key={p} value={p} />)}</datalist>
      <div className="rowc" style={{ flexWrap: 'wrap', gap: 6 }}>{rec.people.slice(0, 6).map(p => <button key={p} type="button" className="bt sm" onClick={() => save.mutate(p)}>{p}</button>)}</div>
      <div className="rowc"><button className="bt pri sm" disabled={!name.trim() || save.isPending}>{cur?.suggested && name === cur.suggested ? 'Confirm' : 'Save'}</button><button type="button" className="bt sm" onClick={onDone}>Cancel</button></div>
      {save.error ? <ErrorNote error={save.error} /> : null}
    </form>
  );
}

/** The voices: confirmed ones plain, guesses violet, unknown ones asking; each opens Name. "N not confirmed". */
export function Speakers({ rec, canName }: { rec: Recording; canName: boolean }) {
  const [open, setOpen] = useState<string | null>(null);
  if (!rec.speakers.length) return null;
  const left = rec.speakers.filter(s => !s.confirmed).length;
  return (
    <div className="col" style={{ gap: 6 }} role="group" aria-label="Speakers">
      <div className="rowc" style={{ flexWrap: 'wrap', gap: 6 }}>
        <span className="k">Speakers</span>
        {rec.speakers.map(s => {
          const v = speakerView(s.label, rec.speakers);
          return <button key={s.label} type="button" className={`spk ${v.state}`} disabled={!canName} onClick={() => setOpen(s.label)} data-speaker={s.label}
            aria-label={v.state === 'named' ? `${s.label}: ${v.name}` : v.state === 'suggested' ? `${s.label}: probably ${v.name}, not confirmed` : `${s.label}: not named`}>
            <i style={{ ['--sc' as string]: speakerColor(s.label) }} />{v.name}{v.state === 'suggested' ? '?' : v.state === 'unknown' ? ', who is this?' : ''}</button>;
        })}
        {left > 0 && <span className="xs" style={{ color: 'var(--amb-tx)' }}>{left} not confirmed</span>}
      </div>
      {open && <NameSpeaker rec={rec} label={open} onDone={() => setOpen(null)} />}
    </div>
  );
}

/** The transcript, searchable: lines with the words, the words marked; names as the speakers say. */
export function Transcript({ lines, rec, canName, onSeek }: { lines: TranscriptLine[]; rec?: Recording | null; canName?: boolean; onSeek?: (at: number) => void }) {
  const [q, setQ] = useState('');
  const sp = rec?.speakers ?? [];
  const shown = useMemo(() => {
    const w = q.trim().toLowerCase();
    return lines.filter(l => !w || l.t.toLowerCase().includes(w));
  }, [lines, q]);
  const hl = (t: string) => {
    const w = q.trim().toLowerCase(), i = w ? t.toLowerCase().indexOf(w) : -1;
    return i < 0 ? t : <>{t.slice(0, i)}<mark>{t.slice(i, i + w.length)}</mark>{t.slice(i + w.length)}</>;
  };
  return (
    <div className="col" style={{ gap: 10 }}>
      {rec && <Speakers rec={rec} canName={!!canName} />}
      <input className="inp" value={q} onChange={e => setQ(e.target.value)} placeholder="Search the transcript" aria-label="Search the transcript" />
      <div role="list" aria-label="Transcript">
        {shown.map((l, i) => {
          const v = speakerView(l.who, sp);
          return (
            <div className="tl2" role="listitem" key={`${l.at}-${i}`}>
              {onSeek ? <button type="button" className="w ts" style={{ textAlign: 'left' }} onClick={() => onSeek(l.at)}>{clock(l.at)}</button> : <span className="w">{clock(l.at)}</span>}
              <span className={`who ${v.state}`} style={{ ['--sc' as string]: speakerColor(l.who) }}><i />{v.name}{v.state === 'suggested' ? '?' : ''}</span>
              <span>{hl(l.t)}</span>
            </div>
          );
        })}
        {!shown.length && <div className="xs mut">{lines.length ? 'No line has those words.' : 'No transcript yet.'}</div>}
      </div>
    </div>
  );
}

/**
 * Ask about it: the question opens the assistant panel on this meeting (one AI surface everywhere). It answers from the
 * transcript and minutes, says who said it and when, and its source plays the meeting from that moment.
 */
export function AskMeeting({ meetingId, title, chips }: { meetingId: string; title: string; chips: string[] }) {
  const [text, setText] = useState('');
  const panel = useAssistantPanel();
  const send = (q: string) => { const v = q.trim(); if (v.length < 2) return; setText(''); panel.open({ activityId: meetingId, label: title, prompt: v, send: true }); };
  return (
    <div className="chatbox">
      <div className="msg ai">Ask anything about this meeting. Answers come from the transcript and minutes, with the time it was said.</div>
      <div className="rowc" style={{ flexWrap: 'wrap', gap: 6 }}>{chips.map(c => <button key={c} type="button" className="bt sm" onClick={() => send(c)}>{c}</button>)}</div>
      <form className="aibar askmeet" onSubmit={(e: FormEvent) => { e.preventDefault(); send(text); }}>
        <input value={text} onChange={e => setText(e.target.value)} placeholder="Ask about this meeting" aria-label="Ask about this meeting" />
        <button className="bt sm" disabled={text.trim().length < 2}>Ask</button>
      </form>
    </div>
  );
}
